import {
    createEmptyGardenEnvelope,
    createGardenDeviceId,
    createNextGardenEnvelope,
    normalizeGardenEnvelope,
} from '../../domain/garden/gardenEnvelope';
import { readSetting, writeSetting } from '../../infrastructure/storage/localStore';

/**
 * The garden's storage buckets (SPEC-022 D-7). Split from `useGarden` along the
 * mechanism/policy seam: this module knows WHERE a garden lives, the provider
 * decides WHEN to move one.
 *
 * Two buckets, deliberately different shapes:
 *
 *   - The guest garden is the plain id list SPEC-014 shipped, under the same
 *     key. No migration happened and none is owed — a visitor who never signs
 *     in keeps exactly the storage they always had.
 *   - An account garden is a sync envelope under `garden:{uid}`, because only
 *     a synced garden needs revisions.
 */

export const GARDEN_SETTING_KEY = 'garden';
const DEVICE_ID_SETTING_KEY = 'garden.deviceId';

/**
 * Anything can be in storage — a half-written value, or a key another version of
 * the app wrote — so the stored list is parsed defensively and a bad value reads
 * as an empty garden rather than taking the screen down on first paint.
 */
export function parseGarden(raw) {
    if (!raw) {
        return [];
    }

    try {
        const parsed = JSON.parse(raw);

        return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
    } catch (error) {
        return [];
    }
}

export function readGarden(read = readSetting) {
    return parseGarden(read(GARDEN_SETTING_KEY));
}

export function writeGuestGarden(plantIds) {
    return writeSetting(GARDEN_SETTING_KEY, JSON.stringify(plantIds));
}

/**
 * One id per device, minted on first use — it is how the envelope says which
 * device wrote what, and the rules check its shape.
 */
export function readDeviceId() {
    const stored = readSetting(DEVICE_ID_SETTING_KEY);

    if (stored) {
        return stored;
    }

    const created = createGardenDeviceId();

    writeSetting(DEVICE_ID_SETTING_KEY, created);

    return created;
}

function accountSettingKey(uid) {
    return `${GARDEN_SETTING_KEY}:${uid}`;
}

export function readAccountEnvelope(uid, { deviceId, now }) {
    const raw = readSetting(accountSettingKey(uid));

    if (!raw) {
        return null;
    }

    try {
        return normalizeGardenEnvelope(JSON.parse(raw), { deviceId, now });
    } catch (error) {
        return null;
    }
}

export function writeAccountEnvelope(uid, envelope) {
    return writeSetting(accountSettingKey(uid), JSON.stringify(envelope));
}

/**
 * First sign-in on this device: the guest list seeds the account bucket as a
 * pending envelope, so what was saved before signing in uploads rather than
 * vanishes (SPEC-022 D-7). Copy, never move — the guest key survives, and a
 * later sign-out finds it exactly as it was.
 */
export function createSeededAccountEnvelope({ deviceId, now }) {
    const guestPlantIds = readGarden();
    const empty = createEmptyGardenEnvelope({ deviceId, now });

    return guestPlantIds.length > 0
        ? createNextGardenEnvelope(empty, { plantIds: guestPlantIds, deviceId, now })
        : empty;
}
